import { computed, inject, Injectable, signal } from '@angular/core';
import { INotificationMessage, NotificationMessage } from '../models/notification.message.model';
import { IInboxNotificationResponse } from '../../notification-management/notifications/dtos/responses/inbox-notification-response';
import { MyNotificationsApiClientProvider } from '../../notification-management/notifications/clients/my-notifications-api.client.provider';
import { NotificationChannelEnum } from '../../notification-management/notifications/enums/notification-channel.enum';
import { StorageService } from '../../../core/data-access/storages/session-local-storages/session-local-storage.service';
import { Logger } from '../../../core/utilities/logging/logger';
import { UserContext } from '../../../user-management/auth-management/authentication/models/user-context.model';
import { UserContextService } from '../../../user-management/auth-management/authentication/services/user-context.service';

@Injectable({
    providedIn: 'root'
})
export class NotificationService {
    private static readonly STORAGE_KEY = 'notifications';

    /** The bell lists push notifications only; the feed also holds the user's emails. */
    private static readonly FEED_FILTER = { channels: [NotificationChannelEnum.PUSH_NOTIFICATION] };

    /** How many of the newest notifications the bell loads from the server. */
    private static readonly FEED_PAGE_SIZE = 20;

    private readonly myNotificationsClientProvider = inject(MyNotificationsApiClientProvider);

    notifications = signal<NotificationMessage<any>[]>([]);

    unReadNotifications = computed(() => {
        return this.notifications().filter(notification => !notification.isReaded);
    });

    /** The server's unseen count, which covers the whole feed rather than only the loaded page. */
    unreadCount = signal(0);

    constructor() {
        this.loadNotificationsFromStorage();
        this.saveNotificationsToStorage();
    }

    /** Replaces the list and the badge with the server's: the newest notifications and the unseen count. */
    refresh(): void {
        const client = this.myNotificationsClientProvider.getClient();

        client
            .get({ ...NotificationService.FEED_FILTER, page: 1, pageSize: NotificationService.FEED_PAGE_SIZE })
            .subscribe({
                next: page => {
                    this.notifications.set(NotificationMessage.createMany(page.items.map(NotificationService.toNotificationMessage)));
                    this.saveNotificationsToStorage();
                },
                error: error => Logger.warn('Failed to load notifications', error)
            });

        client.getUnseenCount(NotificationService.FEED_FILTER).subscribe({
            next: response => this.unreadCount.set(response.count),
            error: error => Logger.warn('Failed to load the unseen notifications count', error)
        });
    }

    getAllNotifications(): NotificationMessage<any>[] {
        return NotificationMessage.createMany(this.notifications());
    }

    addNotification<TContent>(message: INotificationMessage<TContent>): void {
        const exists = this.notifications().some(notification => notification.id === message.id);

        if (exists) {
            return;
        }

        this.notifications.update(notifications => [NotificationMessage.create(message), ...notifications]);
        this.sortNotificationsByDate();
        this.saveNotificationsToStorage();

        if (!message.isReaded) {
            this.unreadCount.update(count => count + 1);
        }
    }

    markNotificationAsRead(id: string): void {
        const notification = this.notifications().find(n => n.id === id);

        if (!notification || notification.isReaded) {
            return;
        }

        this.notifications.update(notifications => {
            notification.markAsReaded();
            return [...notifications];
        });
        this.saveNotificationsToStorage();
        this.unreadCount.update(count => Math.max(count - 1, 0));

        this.myNotificationsClientProvider
            .getClient()
            .markAsSeen(id)
            .subscribe({ error: error => Logger.warn('Failed to mark the notification as seen', error) });
    }

    markAllNotificationsAsRead(): void {
        this.notifications.update(notifications => notifications.map(n => n.markAsReaded()));
        this.saveNotificationsToStorage();
        this.unreadCount.set(0);

        this.myNotificationsClientProvider
            .getClient()
            .markAllAsSeen(NotificationService.FEED_FILTER)
            .subscribe({ error: error => Logger.warn('Failed to mark all notifications as seen', error) });
    }

    clearNotification(id: string): void {
        this.notifications.update(notifications => notifications.filter(n => n.id !== id));
        this.saveNotificationsToStorage();
    }

    clearAllNotifications(): void {
        this.notifications.set([]);
        this.saveNotificationsToStorage();
    }

    getNotificationContent<T>(id: string): T | undefined {
        const notification = this.notifications().find(n => n.id === id);
        return notification ? (notification.content as T) : undefined;
    }

    getNotificationsByType(type: string): NotificationMessage<any>[] {
        return this.notifications().filter(notification => notification.isOfType(type));
    }

    getNotificationCount(): number {
        return this.notifications().length;
    }

    sortNotificationsByDate(descending: boolean = true): void {
        this.notifications.update(notifications =>
            [...notifications].sort((a, b) => {
                let notificationA = NotificationMessage.create(a);
                let notificationB = NotificationMessage.create(b);

                return descending
                    ? notificationB.createdOnDate.getTime() - notificationA.createdOnDate.getTime()
                    : notificationA.createdOnDate.getTime() - notificationB.createdOnDate.getTime();
            })
        );
    }

    private loadNotificationsFromStorage(): void {
        const storedNotifications = StorageService.getItem<INotificationMessage<any>[]>(this.getNotificaitonKey()) || [];
        this.notifications.set(NotificationMessage.createMany(storedNotifications));
    }

    private saveNotificationsToStorage(): void {
        const notifications = this.notifications();
        Logger.info('Notifications to be saved', notifications);
        StorageService.setItem<any>(this.getNotificaitonKey(), notifications, true);
    }

    private getNotificaitonKey(): string {
        return `User-${UserContextService.getUserContext()?.id}-${NotificationService.STORAGE_KEY}`;
    }

    /** A feed item in the shape the bell shows; the same mapping the hub uses for a live ReceiveNotification. */
    private static toNotificationMessage(notification: IInboxNotificationResponse): INotificationMessage<any> {
        return {
            id: notification.id,
            type: notification.sourceType ?? notification.channel,
            title: notification.title ?? '',
            details: notification.body,
            content: { sourceId: notification.sourceId },
            notifier: notification.channel,
            createdOn: new Date(notification.createdOnUtc).toISOString(),
            isReaded: notification.isSeen
        };
    }
}
